import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  Icon,
  IconButton,
  Collapse,
  Badge,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  useDisclosure,
} from "@chakra-ui/react";
import {
  CloseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import {
  FaTruck,
  FaArrowRight,
  FaWalking,
  FaExclamationTriangle,
  FaPrint,
} from "react-icons/fa";
import { IoMdPricetag } from "react-icons/io";
import { useRef, useState, useEffect } from "react";
import { GARAAZ_PERSONNEL_SUB_TYPES } from "../../../types";
import { DeliveryModes } from "../../../types";
import { useToast } from "@chakra-ui/react";
import { getGrazeons } from "../services";
import { RiderDetails } from "../types";
import { GroupSelection } from "../store/useR2DOrdersStore";
import {
  InvoiceMediaIdentifier,
  normalizeInvoiceMediaId,
  printGaraazInvoices,
} from "../../../utils/invoicePrintUtils";
import { extractInvoiceMediaIdsFromGroup } from "../../../utils/invoiceExtractionUtils";
import {
  handleInvoicePrintError,
  showInvoicePrintSuccess,
} from "../../../utils/invoicePrintErrorHandler";

export interface SelectedRiderDetails {
  name?: string;
  phone?: string;
  vehicleNumber?: string;
  trackingUrl?: string;
}

interface QuoteBarProps {
  activeGroups: Array<[string, GroupSelection]>;
  selectedOrders: string[];
  quoteCardsExpanded: boolean;
  allowMultiCustomerShipment?: boolean;
  onToggleExpansion: () => void;
  onCloseGroup: (groupKey: string) => void;
  onBookPorter: (groupKey: string) => Promise<void>;
  onSelfDispatch: (
    groupKey: string,
    rider?: SelectedRiderDetails
  ) => Promise<void>;
  onSelfPickup: (
    groupKey: string,
    picker?: SelectedRiderDetails
  ) => Promise<void>;
  riders: RiderDetails[] | undefined;
  ridersLoading: boolean;
}

export default function QuoteBar({
  activeGroups,
  selectedOrders,
  quoteCardsExpanded,
  allowMultiCustomerShipment = false,
  onToggleExpansion,
  onCloseGroup,
  onBookPorter,
  onSelfDispatch,
  onSelfPickup,
  riders,
  ridersLoading,
}: QuoteBarProps) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const riderDialog = useDisclosure();
  const [pendingGroupKey, setPendingGroupKey] = useState<string>("");
  const [defaultRiders, setDefaultRiders] = useState<
    RiderDetails[] | undefined
  >(riders);
  const [isLoading, setIsLoading] = useState<Boolean>(false);
  const [riderDetails, setRiderDetails] = useState<RiderDetails>({
    name: "",
    phone: "",
    vehicleNumber: "",
    trackingUrl: "",
  });
  const pickerDialog = useDisclosure();
  const [pickerDetails, setPickerDetails] = useState<{
    name: string;
    phone: string;
  }>({ name: "", phone: "" });
  const toast = useToast();
  const [printingGroupKey, setPrintingGroupKey] = useState<string | null>(null);

  useEffect(() => {
    if (riderDialog.isOpen && defaultRiders === undefined && !ridersLoading) {
      (async () => {
        try {
          const res = await getGrazeons({
            userSubType: GARAAZ_PERSONNEL_SUB_TYPES.USTAAD,
          });

          const list: RiderDetails[] = Array.isArray(res?.data)
            ? res.data.map((item: any) => {
                const user = item?.user || {};
                const name = [user.firstName, user.lastName]
                  .filter(Boolean)
                  .join(" ");
                return {
                  id: user._id,
                  name,
                  phone: user.mobileNumber || "",
                } as RiderDetails;
              })
            : [];
          setDefaultRiders(list);
        } catch (e) {
          setDefaultRiders([]);
        }
      })();
    }
  }, [riderDialog.isOpen, riders, ridersLoading]);

  const openSelfDispatchDialog = (groupKey: string) => {
    setPendingGroupKey(groupKey);
    setRiderDetails({
      name: "",
      phone: "",
      vehicleNumber: "",
      trackingUrl: "",
    });
    riderDialog.onOpen();
  };

  const handleSelectRider = (riderPhone: string) => {
    const rider = defaultRiders?.find((r) => r.phone === riderPhone);
    if (rider) {
      setRiderDetails((prev) => ({
        ...prev,
        name: rider.name || "",
        phone: rider.phone || "",
      }));
    }
  };

  const handleConfirmSelfDispatch = async () => {
    if (isLoading) return;

    if (
      riderDetails.trackingUrl?.trim() &&
      riderDetails.trackingUrl.trim().includes(" ")
    ) {
      return;
    }

    setIsLoading(true);
    const rider =
      riderDetails.name ||
      riderDetails.phone ||
      riderDetails.vehicleNumber ||
      riderDetails.trackingUrl ||
      undefined
        ? {
            name: riderDetails.name || undefined,
            phone: riderDetails.phone || undefined,
            vehicleNumber: riderDetails.vehicleNumber || undefined,
            trackingUrl: riderDetails.trackingUrl?.trim() || undefined,
          }
        : undefined;
    try {
      await onSelfDispatch(pendingGroupKey, rider);
      riderDialog.onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const openSelfPickupDialog = (groupKey: string) => {
    setPendingGroupKey(groupKey);
    setPickerDetails({ name: "", phone: "" });
    pickerDialog.onOpen();
  };

  const handleConfirmSelfPickup = async () => {
    if (isLoading) return;

    if (!pickerDetails.name.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter the picker's name",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (pickerDetails.name.trim().length > 40) {
      toast({
        title: "Name Too Long",
        description: "Name must be 40 characters or less",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    if (!pickerDetails.phone.trim()) {
      toast({
        title: "Phone Number Required",
        description: "Please enter the picker's phone number",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(pickerDetails.phone.replace(/\D/g, ""))) {
      toast({
        title: "Invalid Phone Number",
        description: "Please enter a valid phone number (exactly 10 digits)",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      await onSelfPickup(pendingGroupKey, pickerDetails);
      pickerDialog.onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintGroupInvoices = async (
    groupKey: string,
    invoiceMediaIds: InvoiceMediaIdentifier[]
  ) => {
    setPrintingGroupKey(groupKey);
    try {
      const result = await printGaraazInvoices(invoiceMediaIds);
      showInvoicePrintSuccess(result.printableCount, toast);
    } catch (error) {
      handleInvoicePrintError(error, toast, {
        title: "Unable to print invoices",
      });
    } finally {
      setPrintingGroupKey(null);
    }
  };

  const checkScrollPosition = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const checkScrollPositionDelayed = () => {
    setTimeout(checkScrollPosition, 50);
  };
  useEffect(() => {
    if (quoteCardsExpanded) {
      checkScrollPositionDelayed();
    } else {
      checkScrollPosition();
    }

    const container = scrollContainerRef.current;
    if (container) {
      const resizeObserver = new ResizeObserver(() => {
        checkScrollPosition();
      });
      resizeObserver.observe(container);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, [activeGroups, quoteCardsExpanded]);

  if (activeGroups.length === 0) {
    return null;
  }

  const getDeliveryModeButtons = (group: GroupSelection, groupKey: string) => {
    const isConsolidated =
      allowMultiCustomerShipment &&
      (group.isConsolidatedGroup || groupKey.startsWith("supplier__"));

    if (isConsolidated) {
      return {
        showBookPorter: false,
        showSelfDispatch: true,
        showSelfPickup: false,
        showMultipleModes: false,
      };
    }

    const deliveryModes = Object.values(
      group.orderDeliveryModes || {}
    ) as DeliveryModes[];
    const uniqueDeliveryModes = Array.from(new Set(deliveryModes));

    if (uniqueDeliveryModes.length === 0) {
      return {
        showBookPorter: false,
        showSelfDispatch: false,
        showSelfPickup: false,
        showMultipleModes: false,
      };
    }

    if (uniqueDeliveryModes.length === 1) {
      const mode = uniqueDeliveryModes[0];
      if (mode === DeliveryModes.SELF_PICKUP) {
        return {
          showBookPorter: false,
          showSelfDispatch: false,
          showSelfPickup: true,
          showMultipleModes: false,
        };
      } else if (mode === DeliveryModes.DOOR_DELIVERY) {
        return {
          showBookPorter: true,
          showSelfDispatch: true,
          showSelfPickup: false,
          showMultipleModes: false,
        };
      }
    }

    return {
      showBookPorter: false,
      showSelfDispatch: false,
      showSelfPickup: false,
      showMultipleModes: true,
    };
  };

  return (
    <>
      <Box
        bg="white"
        borderRadius="xl"
        shadow="0 4px 20px rgba(0, 0, 0, 0.08)"
        border="1px solid"
        borderColor="gray.100"
        position="sticky"
        top="0"
        zIndex="10"
        mb={6}
      >
        {/* Header with Summary and Toggle */}
        <Box
          p={2}
          borderBottom={quoteCardsExpanded ? "1px solid" : "none"}
          borderColor="gray.100"
          cursor="pointer"
          onClick={onToggleExpansion}
          _hover={{ bg: "gray.50" }}
          transition="background-color 0.2s"
        >
          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Badge
                bg="blue.500"
                color="white"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontWeight="600"
                border="1px"
                borderColor="gray.200"
                lineHeight={"normal"}
                textTransform={"none"}
                fontSize="sm"
              >
                {activeGroups.length} Quote
                {activeGroups.length !== 1 ? "s" : ""}
              </Badge>

              <Badge
                bg="white"
                color="gray.700"
                variant="subtle"
                px={3}
                py={2}
                borderRadius="full"
                fontWeight="600"
                border="1px"
                borderColor="gray.200"
                lineHeight={"normal"}
                textTransform={"none"}
                fontSize="sm"
              >
                {selectedOrders.length} Orders
              </Badge>
            </HStack>

            <HStack spacing={7}>
              <IconButton
                aria-label="Toggle expansion"
                icon={
                  quoteCardsExpanded ? (
                    <ChevronUpIcon boxSize={6} />
                  ) : (
                    <ChevronDownIcon boxSize={6} />
                  )
                }
                variant="ghost"
                size="lg"
                _hover={{
                  bg: "none",
                }}
                px={7}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpansion();
                }}
              />
            </HStack>
          </HStack>
        </Box>

        {/* Collapsible Cards Content */}
        <Collapse in={quoteCardsExpanded}>
          <Box p={4} position="relative">
            {/* Enhanced Scroll Buttons */}
            {activeGroups.length > 3 && (
              <>
                {/* Left Scroll Button - Hidden when at start */}
                {canScrollLeft && (
                  <Box
                    position="absolute"
                    left={0}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={12}
                    paddingLeft={1}
                    paddingRight={1}
                    paddingTop={3}
                    paddingBottom={3}
                    bg="white"
                    borderTopRightRadius="xl"
                    borderBottomRightRadius="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s ease"
                    boxShadow="0 0px 5px #667eea"
                    cursor="pointer"
                    onClick={() => {
                      const container = scrollContainerRef.current;
                      if (container) {
                        container.scrollBy({ left: -400, behavior: "smooth" });
                      }
                    }}
                    _hover={{
                      boxShadow: "0 0px 12px #667eea",
                    }}
                  >
                    <Icon as={ChevronLeftIcon} boxSize={6} color="gray.600" />
                  </Box>
                )}

                {/* Right Scroll Button - Hidden when at end */}
                {canScrollRight && (
                  <Box
                    position="absolute"
                    right={0}
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex={12}
                    paddingLeft={1}
                    paddingRight={1}
                    paddingTop={3}
                    paddingBottom={3}
                    bg="white"
                    borderTopLeftRadius="xl"
                    borderBottomLeftRadius="xl"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="all 0.3s ease"
                    boxShadow="0 0px 5px #667eea"
                    cursor="pointer"
                    onClick={() => {
                      const container = scrollContainerRef.current;
                      if (container) {
                        container.scrollBy({ left: 400, behavior: "smooth" });
                      }
                    }}
                    _hover={{
                      boxShadow: "0 0px 12px #667eea",
                    }}
                  >
                    <Icon as={ChevronRightIcon} boxSize={6} color="gray.600" />
                  </Box>
                )}
              </>
            )}

            <Box
              ref={scrollContainerRef}
              position="relative"
              overflowX="auto"
              overflowY="hidden"
              scrollBehavior="smooth"
              onScroll={checkScrollPosition}
              sx={{
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <HStack spacing={4} align="stretch" minW="max-content" pb={2}>
                {activeGroups.map(([groupKey, group]) => {
                  const invoiceMediaIds = extractInvoiceMediaIdsFromGroup(
                    group.selectedOrders,
                    group.orderDataMap
                  );
                  const hasPrintableInvoices = invoiceMediaIds.some((value) =>
                    Boolean(normalizeInvoiceMediaId(value))
                  );

                  return (
                    <Box
                      key={groupKey}
                      bg="white"
                      borderRadius="lg"
                      p={3}
                      shadow="0 2px 12px rgba(0, 0, 0, 0.06)"
                      border="1px solid"
                      borderColor="gray.200"
                      minW="350px"
                      maxW="350px"
                      position="relative"
                      _hover={{
                        shadow: "0 4px 20px rgba(0, 0, 0, 0.12)",
                        transform: "translateY(-2px)",
                      }}
                      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                    >
                      {/* Printer icon at top left */}
                      <HStack
                        position="absolute"
                        top={2}
                        left={2}
                        spacing={1}
                        zIndex={11}
                      >
                        <Tooltip
                          label={
                            hasPrintableInvoices
                              ? "Print Garaaz invoices"
                              : "No Garaaz invoices available yet"
                          }
                        >
                          <IconButton
                            aria-label="Print Garaaz invoices"
                            icon={<FaPrint />}
                            size="sm"
                            variant="ghost"
                            color="gray.500"
                            isDisabled={
                              !hasPrintableInvoices ||
                              printingGroupKey === groupKey
                            }
                            isLoading={printingGroupKey === groupKey}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrintGroupInvoices(
                                groupKey,
                                invoiceMediaIds
                              );
                            }}
                            _hover={{
                              color: "purple.600",
                              bg: "purple.50",
                            }}
                          />
                        </Tooltip>
                      </HStack>
                      <HStack
                        position="absolute"
                        top={2}
                        right={2}
                        spacing={1}
                        zIndex={11}
                      >
                        <IconButton
                          aria-label="Cancel quote card"
                          icon={<CloseIcon boxSize={2.5} />}
                          size="sm"
                          variant="ghost"
                          onClick={() => onCloseGroup(groupKey)}
                        />
                      </HStack>

                      {/* Card Content */}
                      <VStack spacing={2} align="stretch">
                        {/* Route Info - Compact and Centered */}
                        <VStack spacing={0.5} align="center">
                          <HStack
                            spacing={0}
                            align="center"
                            justify="center"
                            w="100%"
                          >
                            <VStack align="center" flex="1" minW="0">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                From
                              </Text>
                              <Tooltip
                                label={group.supplierName}
                                placement="top"
                                hasArrow
                              >
                                <Text
                                  fontSize="sm"
                                  color="gray.700"
                                  fontWeight="600"
                                  maxW="100px"
                                  noOfLines={1}
                                  cursor="pointer"
                                >
                                  {group.supplierName}
                                </Text>
                              </Tooltip>
                            </VStack>
                            <VStack align="center" spacing={0} px={0}>
                              <Icon
                                as={FaArrowRight}
                                boxSize={3}
                                color="blue.500"
                              />
                            </VStack>
                            <VStack align="center" flex="1" minW="0">
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                To
                              </Text>
                              <Tooltip
                                label={
                                  allowMultiCustomerShipment &&
                                  group.isConsolidatedGroup
                                    ? Object.values(
                                        group.consigneeNames || {}
                                      ).join(", ")
                                    : group.customerName
                                }
                                placement="top"
                                hasArrow
                              >
                                <Text
                                  fontSize="sm"
                                  color="gray.700"
                                  fontWeight="600"
                                  maxW="100px"
                                  noOfLines={1}
                                  cursor="pointer"
                                >
                                  {allowMultiCustomerShipment &&
                                  group.isConsolidatedGroup
                                    ? `${new Set(Object.values(group.consigneeNames || {})).size} Customers`
                                    : group.customerName}
                                </Text>
                              </Tooltip>
                            </VStack>
                          </HStack>
                        </VStack>
                        {/* Quote Info Grid - Compact with Vertical Lines */}
                        {group.porterQuoteLoading ? (
                          <Box
                            bg="blue.50"
                            borderRadius="md"
                            p={1.5}
                            textAlign="center"
                          >
                            <HStack justify="center" spacing={2}>
                              <Spinner size="sm" color="blue.500" />
                              <Text
                                fontSize="xs"
                                color="blue.700"
                                fontWeight="500"
                              >
                                Getting quotes...
                              </Text>
                            </HStack>
                          </Box>
                        ) : Array.from(
                            new Set(
                              Object.values(
                                group.orderDeliveryModes || {}
                              ) as DeliveryModes[]
                            )
                          ).length === 1 &&
                          (
                            Object.values(
                              group.orderDeliveryModes || {}
                            ) as DeliveryModes[]
                          )[0] === DeliveryModes.DOOR_DELIVERY &&
                          group.porterQuote?.vehicles &&
                          group.porterQuote.vehicles.length > 0 ? (
                          <HStack
                            justify="space-between"
                            align="center"
                            spacing={0}
                            borderRadius="md"
                            px={5}
                            py={2}
                          >
                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                Orders
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="blue.600"
                              >
                                {group.selectedOrders.length}
                              </Text>
                            </VStack>

                            <Box w="1px" h="6" bg="gray.300" />

                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                EST. Fare
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="green.600"
                              >
                                ₹
                                {(() => {
                                  const fares = group.porterQuote?.vehicles
                                    ?.map(
                                      (vehicle: any) =>
                                        vehicle?.fare?.minor_amount
                                    )
                                    ?.filter(
                                      (fare: any) =>
                                        fare !== undefined && fare !== null
                                    );
                                  if (!fares || fares.length === 0) return 0;
                                  const minFare = Math.min(...fares);
                                  return minFare / 100;
                                })()}
                              </Text>
                            </VStack>

                            <Box w="1px" h="6" bg="gray.300" />

                            <VStack align="center" spacing={0.5}>
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                fontWeight="500"
                                textTransform="uppercase"
                              >
                                ETA
                              </Text>
                              <Text
                                fontSize="md"
                                fontWeight="bold"
                                color="orange.600"
                              >
                                {(() => {
                                  const etas = group.porterQuote?.vehicles
                                    ?.map((vehicle: any) => vehicle?.eta?.value)
                                    ?.filter(
                                      (eta: any): eta is number =>
                                        eta !== undefined && eta !== null
                                    );
                                  if (!etas || etas.length === 0) return "N/A";
                                  const minEta = Math.min(...etas);
                                  const firstVehicle =
                                    group.porterQuote!.vehicles[0];
                                  const unit =
                                    (firstVehicle as any)?.eta?.unit || "mins";
                                  return `${minEta} ${unit}`;
                                })()}
                              </Text>
                            </VStack>
                          </HStack>
                        ) : (
                          (() => {
                            const deliveryModes = Object.values(
                              group.orderDeliveryModes || {}
                            ) as DeliveryModes[];
                            const uniqueDeliveryModes = Array.from(
                              new Set(deliveryModes)
                            );

                            if (
                              uniqueDeliveryModes.length === 1 &&
                              uniqueDeliveryModes[0] ===
                                DeliveryModes.DOOR_DELIVERY
                            ) {
                              return (
                                <Box
                                  bg="yellow.50"
                                  borderRadius="lg"
                                  px={2}
                                  py={1.5}
                                  border="1px solid"
                                  borderColor="yellow.200"
                                  minH="64px"
                                  display="flex"
                                  alignItems="center"
                                  w="100%"
                                  borderLeftWidth="4px"
                                  borderLeftColor="yellow.400"
                                >
                                  <HStack justify="center" spacing={3} w="100%">
                                    <HStack spacing={3}>
                                      <VStack spacing={0} align="center">
                                        <HStack spacing={2}>
                                          <Icon
                                            as={IoMdPricetag}
                                            boxSize={4}
                                            color="yellow.700"
                                          />
                                          <Text
                                            fontSize="sm"
                                            color="yellow.900"
                                            fontWeight="700"
                                          >
                                            Quote pending
                                          </Text>
                                          <Badge
                                            colorScheme="yellow"
                                            variant="subtle"
                                          >
                                            Door Delivery
                                          </Badge>
                                        </HStack>
                                        <Text
                                          fontSize="xs"
                                          color="yellow.800"
                                          fontWeight="500"
                                        >
                                          Waiting for fares & ETA
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </HStack>
                                </Box>
                              );
                            }

                            if (
                              uniqueDeliveryModes.length === 1 &&
                              uniqueDeliveryModes[0] ===
                                DeliveryModes.SELF_PICKUP
                            ) {
                              return (
                                <Box
                                  bg="green.50"
                                  borderRadius="lg"
                                  px={2}
                                  py={1.5}
                                  border="1px solid"
                                  borderColor="green.200"
                                  minH="64px"
                                  display="flex"
                                  alignItems="center"
                                  w="100%"
                                  borderLeftWidth="4px"
                                  borderLeftColor="green.400"
                                >
                                  <HStack justify="center" spacing={3} w="100%">
                                    <HStack spacing={3} align="center">
                                      <Icon
                                        as={FaWalking}
                                        boxSize={4}
                                        color="green.700"
                                      />
                                      <VStack spacing={0} align="start">
                                        <HStack spacing={2}>
                                          <Text
                                            fontSize="sm"
                                            color="green.900"
                                            fontWeight="700"
                                          >
                                            No quote needed
                                          </Text>
                                          <Badge
                                            colorScheme="green"
                                            variant="subtle"
                                          >
                                            Self Pickup
                                          </Badge>
                                        </HStack>
                                        <Text
                                          fontSize="xs"
                                          color="green.800"
                                          fontWeight="500"
                                        >
                                          Proceed with Self Pickup flow
                                        </Text>
                                      </VStack>
                                    </HStack>
                                  </HStack>
                                </Box>
                              );
                            }

                            return (
                              <Box
                                bg="blue.50"
                                borderRadius="lg"
                                px={2}
                                py={1.5}
                                border="1px solid"
                                borderColor="blue.200"
                                minH="105px"
                                display="flex"
                                alignItems="center"
                                w="100%"
                                borderLeftWidth="4px"
                                borderLeftColor="blue.400"
                              >
                                <HStack justify="center" spacing={3} w="100%">
                                  <HStack spacing={3}>
                                    <VStack spacing={0} align="center">
                                      <HStack spacing={2}>
                                        <Icon
                                          as={FaExclamationTriangle}
                                          boxSize={4}
                                          color="blue.700"
                                        />
                                        <Text
                                          fontSize="sm"
                                          color="blue.900"
                                          fontWeight="700"
                                        >
                                          Multiple Delivery Modes Detected
                                        </Text>
                                      </HStack>
                                      <Text
                                        fontSize="xs"
                                        color="blue.800"
                                        fontWeight="500"
                                      >
                                        Unify delivery modes to enable actions
                                      </Text>
                                    </VStack>
                                  </HStack>
                                </HStack>
                              </Box>
                            );
                          })()
                        )}
                        <HStack spacing={2} align="stretch">
                          {(() => {
                            const buttons = getDeliveryModeButtons(group, groupKey);
                            const buttonElements: React.ReactNode[] = [];

                            if (buttons.showMultipleModes) {
                            } else {
                              if (buttons.showBookPorter) {
                                buttonElements.push(
                                  <Button
                                    key="book-porter"
                                    onClick={() => onBookPorter(groupKey)}
                                    bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                                    color="white"
                                    size="sm"
                                    fontSize="xs"
                                    fontWeight="600"
                                    borderRadius="md"
                                    leftIcon={<FaTruck />}
                                    isLoading={group.porterQuoteLoading}
                                    loadingText="Getting Quote..."
                                    disabled={group.porterQuoteLoading}
                                    flex={1}
                                    _hover={{
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 15px rgba(102, 126, 234, 0.4)",
                                      bg: "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                                    }}
                                    _active={{
                                      transform: "translateY(0px)",
                                    }}
                                    _disabled={{
                                      opacity: 0.6,
                                      cursor: "not-allowed",
                                    }}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                  >
                                    Book Porter
                                  </Button>
                                );
                              }

                              if (buttons.showSelfDispatch) {
                                buttonElements.push(
                                  <Button
                                    key="self-dispatch"
                                    onClick={() =>
                                      openSelfDispatchDialog(groupKey)
                                    }
                                    bg="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                    color="white"
                                    size="sm"
                                    fontSize="xs"
                                    fontWeight="600"
                                    borderRadius="md"
                                    leftIcon={<FaTruck />}
                                    flex={1}
                                    _hover={{
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 15px rgba(240, 147, 251, 0.4)",
                                      bg: "linear-gradient(135deg, #e085f0 0%, #f04a5c 100%)",
                                    }}
                                    _active={{
                                      transform: "translateY(0px)",
                                    }}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                  >
                                    Self Dispatch
                                  </Button>
                                );
                              }

                              if (buttons.showSelfPickup) {
                                buttonElements.push(
                                  <Button
                                    key="self-pickup"
                                    onClick={() =>
                                      openSelfPickupDialog(groupKey)
                                    }
                                    bg="linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%)"
                                    color="white"
                                    size="sm"
                                    fontSize="xs"
                                    fontWeight="600"
                                    borderRadius="md"
                                    leftIcon={<FaWalking />}
                                    flex={1}
                                    _hover={{
                                      transform: "translateY(-1px)",
                                      boxShadow:
                                        "0 4px 15px rgba(86, 171, 47, 0.4)",
                                      bg: "linear-gradient(135deg, #4a9a2a 0%, #96d4b8 100%)",
                                    }}
                                    _active={{
                                      transform: "translateY(0px)",
                                    }}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                  >
                                    Self Pickup
                                  </Button>
                                );
                              }
                            }

                            return buttonElements;
                          })()}
                        </HStack>
                      </VStack>
                    </Box>
                  );
                })}
              </HStack>
            </Box>
          </Box>
        </Collapse>
      </Box>

      <Modal
        isOpen={riderDialog.isOpen}
        onClose={riderDialog.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Rider details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>Select saved rider</FormLabel>
                <Select
                  placeholder="Select rider"
                  onChange={(e) => handleSelectRider(e.target.value)}
                >
                  {defaultRiders?.map((r) => (
                    <option
                      key={r.phone}
                      value={r.phone}
                    >{`${r.name} (${r.phone})`}</option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Rider name</FormLabel>
                <Input
                  value={riderDetails.name}
                  isReadOnly
                  isDisabled
                  placeholder="Select rider to populate"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Rider phone</FormLabel>
                <Input
                  value={riderDetails.phone}
                  isReadOnly
                  isDisabled
                  placeholder="Select rider to populate"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Vehicle number</FormLabel>
                <Input
                  value={riderDetails.vehicleNumber}
                  onChange={(e) => {
                    const v = e.target.value;
                    setRiderDetails((prev) => ({ ...prev, vehicleNumber: v }));
                  }}
                  placeholder="e.g. AB-12-AB-1234"
                />
              </FormControl>
              <FormControl
                isInvalid={riderDetails.trackingUrl?.trim().includes(" ")}
              >
                <FormLabel>Tracking URL</FormLabel>
                <Input
                  value={riderDetails.trackingUrl}
                  onChange={(e) => {
                    const url = e.target.value;
                    setRiderDetails((prev) => ({ ...prev, trackingUrl: url }));
                  }}
                  placeholder="Enter tracking URL"
                  maxLength={500}
                />
                {riderDetails.trackingUrl?.trim().includes(" ") && (
                  <FormHelperText color="red.500">
                    Tracking URL cannot contain spaces
                  </FormHelperText>
                )}
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={riderDialog.onClose}>
                Cancel
              </Button>
              <Button
                bg={"blue.500"}
                onClick={handleConfirmSelfDispatch}
                isLoading={!!isLoading}
                isDisabled={
                  !!isLoading || riderDetails.trackingUrl?.trim().includes(" ")
                }
                loadingText="Dispatching..."
              >
                Continue
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={pickerDialog.isOpen}
        onClose={pickerDialog.onClose}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Picker Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Picker Name</FormLabel>
                <Input
                  value={pickerDetails.name}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickerDetails((prev) => ({ ...prev, name: v }));
                  }}
                  placeholder="Enter picker name"
                  isInvalid={pickerDetails.name.trim() === ""}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={pickerDetails.phone}
                  onChange={(e) => {
                    const v = e.target.value;
                    setPickerDetails((prev) => ({ ...prev, phone: v }));
                  }}
                  placeholder="Enter phone number"
                  type="tel"
                  isInvalid={pickerDetails.phone.trim() === ""}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={pickerDialog.onClose}>
                Cancel
              </Button>
              <Button
                bg={"blue.500"}
                onClick={handleConfirmSelfPickup}
                isLoading={!!isLoading}
                isDisabled={!!isLoading}
                loadingText="Processing..."
              >
                Continue
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
